import { Phone } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Table, type Column } from '@/components/ui/Table';
import { cn, getNameInitials } from '@/lib/utils';
import type { TransportDriver } from '@/types/transport';
import { DRIVER_STATUS, ROUTE_TONE, expiryTone, toDateLabel, toRelativeDayLabel } from '../constants';
import { ROWS_PER_PAGE, useRegister } from '../useRegister';
import { RegisterToolbar } from './RegisterToolbar';
import { RowActions } from './RowActions';

/** What a status in the address may be — every one this register's rows can hold. */
const DRIVER_STATUSES = Object.keys(DRIVER_STATUS);

export interface DriversTabProps {
  drivers: TransportDriver[];
  onOpenDriver?: (driver: TransportDriver) => void;
  onEditDriver?: (driver: TransportDriver) => void;
  onDriverMenu?: (driver: TransportDriver) => void;
}

/**
 * Who drives, what they hold and where they are.
 *
 * No card grid, unlike Routes and Vehicles — and that is the design being
 * right rather than unfinished. A route has a shape and a vehicle has a kind,
 * both of which a card can show at a glance; a driver has a name, a number and
 * a licence, which are three pieces of text that a row says better than a tile.
 */
export function DriversTab({
  drivers,
  onOpenDriver,
  onEditDriver,
  onDriverMenu,
}: DriversTabProps) {
  const register = useRegister({
    prefix: 'drivers',
    statuses: DRIVER_STATUSES,
    rows: drivers,
    fieldsOf: (driver) => [
      driver.fullName,
      driver.employeeCode,
      driver.mobile,
      driver.licenceNumber,
      driver.routeName,
      driver.vehicleNumber,
    ],
    statusOf: (driver) => driver.status,
  });

  const columns: Column<TransportDriver>[] = [
    {
      key: 'driver',
      header: 'Driver name',
      render: (driver) => (
        <div className="flex items-center gap-3">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-[11px] font-black text-white shadow-sm"
            aria-hidden
          >
            {getNameInitials(driver.fullName)}
          </span>
          <div className="min-w-0">
            <p className="truncate font-bold text-ink">{driver.fullName}</p>
            {driver.employeeCode && (
              <p className="truncate text-xs font-semibold text-ink-3">
                Driver ID: {driver.employeeCode}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'mobile',
      header: 'Contact no.',
      render: (driver) => (
        <span className="inline-flex items-center gap-1.5 font-semibold text-ink-2">
          <Phone className="h-3.5 w-3.5 shrink-0 text-ink-3" aria-hidden />
          {driver.mobile}
        </span>
      ),
    },
    {
      key: 'licence',
      header: 'Licence no.',
      hideOnMobile: true,
      render: (driver) => (
        <span className="font-mono text-sm font-semibold text-ink-2">{driver.licenceNumber}</span>
      ),
    },
    {
      key: 'route',
      header: 'Assigned route',
      hideOnMobile: true,
      render: (driver) =>
        driver.routeName ? (
          <span
            className={cn(
              'pill text-[12px] font-bold tracking-label',
              ROUTE_TONE[driver.routeTone ?? 'sky'].soft,
            )}
          >
            {driver.routeCode ? `${driver.routeCode} — ` : ''}
            {driver.routeName}
          </span>
        ) : (
          <span className="font-semibold text-ink-3">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (driver) => (
        <Badge tone={DRIVER_STATUS[driver.status].tone} withDot>
          {DRIVER_STATUS[driver.status].label}
        </Badge>
      ),
    },
    {
      key: 'validity',
      header: 'Licence validity',
      hideOnMobile: true,
      render: (driver) => {
        const tone = expiryTone(driver.licenceExpiry);
        return (
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-ink-2">
              {toDateLabel(driver.licenceExpiry)}
            </p>
            <p
              className={cn(
                'mt-0.5 truncate text-xs font-bold',
                tone === 'crit' ? 'text-crit' : tone === 'warn' ? 'text-warn' : 'text-ok',
              )}
            >
              {toRelativeDayLabel(driver.licenceExpiry)}
            </p>
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      render: (driver) => (
        <RowActions
          subject={driver.fullName}
          onOpen={onOpenDriver && (() => onOpenDriver(driver))}
          onEdit={onEditDriver && (() => onEditDriver(driver))}
          onMore={onDriverMenu && (() => onDriverMenu(driver))}
        />
      ),
    },
  ];

  return (
    <section className="flex flex-col gap-4 rounded-3xl border border-line bg-surface p-4 shadow-sm">
      <RegisterToolbar
        title="All drivers"
        searchPlaceholder="Search drivers…"
        register={register}
        statusOptions={[
          { value: 'ON_DUTY', label: DRIVER_STATUS.ON_DUTY.label },
          { value: 'ON_LEAVE', label: DRIVER_STATUS.ON_LEAVE.label },
          { value: 'INACTIVE', label: DRIVER_STATUS.INACTIVE.label },
        ]}
      />

      <div className="-mx-4">
        <Table
          columns={columns}
          rows={register.page}
          rowKey={(driver) => driver.uuid}
          variant="plain"
          empty={
            <EmptyState
              title={register.query || register.status ? 'No drivers match' : 'No drivers'}
              description={
                register.query || register.status
                  ? 'Nothing matches that search or filter. Try another name, licence or status.'
                  : 'Drivers appear here once the transport roll has been set up.'
              }
            />
          }
        />

        {register.visible.length > 0 && (
          <Pagination
            meta={register.meta}
            onPageChange={register.goToPage}
            itemLabel="drivers"
            showPageNumbers
            limitOptions={ROWS_PER_PAGE}
            onLimitChange={register.setLimit}
          />
        )}
      </div>
    </section>
  );
}
